import { smIconSize } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { isSixDigitCode } from '@/types/utils/sixDigitCode';
import { Code, StatusBadge } from '@components/index';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import Popover, { PopoverProps } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import type { UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, PencilLine } from 'lucide-react';
import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MutationContext, UpdateVariables } from '../../hooks';
import styles from '../../styles.module.css';
import type { InstrumentRunPublic, InstrumentRunStatus, SixDigitCode } from '../../types';
import Description from '../Description/index';
import TextRow from '../TextRow';

import RunMetaPopover from './components/RunMetaPopover';

interface Props {
  run: InstrumentRunPublic;
  updateDescriptionMutation?: UseMutationResult<InstrumentRunPublic, Error, UpdateVariables, MutationContext>;
}

const hoverOpts = { delay: 2000 };
const popoverPositions: PopoverProps['positions'] = ['bottom', 'left'];

const InstrumentRunHeader = ({ run, updateDescriptionMutation }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const formatDate = useDateFormatter();
  const navigate = useNavigate();
  const editButtonHandler = useCallback(
    () =>
      navigate({
        to: '/instrument_runs/new',
        search: {
          id: run?.id,
          mode: 'edit',
        },
      }),
    [navigate, run?.id],
  );

  const infoPopoverButtonOnPressHandler = useCallback(
    () =>
      setPopoverOpen(open => {
        const nextOpen = !open;

        if (nextOpen) setTooltipOpen(false); // Close Tooltip when Popover opens

        return nextOpen;
      }),
    [],
  );

  // Memoize the popover content to avoid re-creating nodes on every render.
  // Popover content extracted to `RunMetaPopover` component (defined above)

  return (
    <Stack className={styles.irDetailsHeader} fullWidth gap={2} orientation="vertical">
      <Stack alignItems="center" fullWidth gap={2} justifyContent="space-between" orientation="horizontal">
        <Tooltip
          content={t('id_label', { id: run.id })}
          hoverOptions={hoverOpts}
          placement="right-start"
          trigger={<Text as="h1">{run.name}</Text>}
        />

        <Stack alignItems="center" gap={8} orientation="horizontal">
          <Button data-testid="edit-instrument-run" onPress={editButtonHandler} size="sm" visual="default">
            <PencilLine size={smIconSize} />
            {t('edit')}
          </Button>

          {isSixDigitCode(run?.code ?? '') && <Code code={run.code as SixDigitCode} id={`${run?.id}-code`} />}
        </Stack>
      </Stack>

      <Grid alignItems="center" columns={2} rows={1}>
        <Stack fullWidth gap={2}>
          {run?.description && (
            <Description
              aria-label={t('description_aria')}
              body={run.description}
              canEdit={true}
              fieldName="description"
              mutation={updateDescriptionMutation}
              title={t('description_label')}
              titlePosition="top-left"
            />
          )}
          <TextRow label={t('type')} value={run.type} />
          <TextRow label={t('species')} value={run.species} />
          {run?.instrument_id && <TextRow label={t('instrument_id')} value={run.instrument_id} />}
        </Stack>

        <Popover
          align="end"
          className={styles.popover}
          content={<RunMetaPopover run={run} formatDate={formatDate} />}
          isOpen={popoverOpen}
          onClickOutside={() => setPopoverOpen(false)}
          padding={8}
          positions={popoverPositions}
          styled
        >
          <Button
            className={styles.statusButton}
            data-testid="dates-popover-trigger"
            onPress={infoPopoverButtonOnPressHandler}
            size="sm"
            visual="unstyled"
          >
            <Tooltip
              content={t('tooltip_status_info')}
              placement="left"
              open={tooltipOpen}
              onOpenChange={setTooltipOpen}
              trigger={<StatusBadge showIcon={ChevronDown} status={run.status as InstrumentRunStatus} />}
            />
          </Button>
        </Popover>
      </Grid>
    </Stack>
  );
};

function propsAreEqual(prev: Readonly<Props>, next: Readonly<Props>) {
  const a = prev.run;
  const b = next.run;

  return (
    a.id === b.id &&
    a.name === b.name &&
    a.description === b.description &&
    a.type === b.type &&
    a.species === b.species &&
    a.instrument_id === b.instrument_id &&
    a.status === b.status &&
    a.created_at === b.created_at &&
    a.updated_at === b.updated_at &&
    a.submitted_at === b.submitted_at &&
    a.completed_at === b.completed_at &&
    a.code === b.code &&
    prev.updateDescriptionMutation === next.updateDescriptionMutation
  );
}

export default memo(InstrumentRunHeader, propsAreEqual);
